import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api, AppConfig, Wallet } from "../../lib/api";
import { money } from "../../lib/format";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { MandateCard } from "./MandateCard";
import { TopUpPanel } from "./TopUpPanel";
import { PaymentsList } from "./PaymentsList";
import { SubscriptionPanel } from "./SubscriptionPanel";
import { PlanSummary } from "./PlanSummary";
import { LedgerTable } from "./LedgerTable";
import { AutoTopUpPanel } from "./AutoTopUpPanel";
import { ChangePasswordPanel } from "./ChangePasswordPanel";
import { GiftPanel } from "./GiftPanel";
import { ReferPanel } from "./ReferPanel";
import { OptionBadge } from "../OptionBadge";

export const CREDIT_TABS = [["ledger", "Current Balance"], ["topup", "Add credit"], ["auto", "Auto top-up"], ["plan", "Recurring plan"], ["gifts", "Gift credit"], ["refer", "Refer a friend"], ["password", "Password"]] as const;

export function CreditSection() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "ledger";
  const { data: wallet } = useQuery<Wallet>({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  const { data: config } = useQuery<AppConfig>({ queryKey: ["config"], queryFn: () => api.get("/config").then((r) => r.data) });
  const mandateActive = wallet?.mandate_status === "active";
  const loading = !wallet;
  const setTab = (v: string) => setParams((p) => { p.set("tab", v); return p; }, { replace: true });

  return (
    <section id="credit" className="scroll-mt-20" data-testid="cellar-page">
      <div className="grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3"><OptionBadge kind="cellar" text="Option 1 · Cellar Plan" /><span className="eyebrow">Your credit</span></div>
          <div className="mt-4 h-1.5 w-24" style={{ background: "var(--cellar)" }} />
          <h2 className="mt-4 font-serif text-3xl font-medium tracking-tight sm:text-4xl" style={{ color: "var(--cellar-dark)" }}>Your two pots</h2>
          <p className="mt-3 max-w-md text-sm text-neutral-600">Two Direct Debits, two purposes. Both are yours to spend — we just keep them apart so you can see what each is building.</p>
          <div className="mt-8 grid gap-px border border-border sm:grid-cols-2" data-testid="credit-pots">
            <div className="border-t-4 bg-[var(--cellar-bg)] p-6 sm:border-r sm:border-border" style={{ borderTopColor: "var(--cellar)" }} data-testid="pot-cellar">
              <div className="eyebrow" style={{ color: "var(--cellar-dark)" }}>Cellar credit</div>
              <div className="mt-3 font-serif text-4xl tabular-nums tracking-tight lg:text-5xl" data-testid="cellar-balance">{wallet ? money(wallet.balance_pence) : "—"}</div>
              <p className="mt-3 text-xs leading-relaxed text-neutral-600">Build a balance to spend on any wine, tasting or gift. Topped up by your cellar Direct Debit, card or bank payment.</p>
              <div className="mt-3 text-xs text-neutral-500" data-testid="cellar-pending">{wallet && wallet.pending_pence > 0 ? `${money(wallet.pending_pence)} awaiting GoCardless confirmation` : "Nothing pending"}</div>
            </div>
            <div className="border-t-4 bg-[var(--club-bg)] p-6" style={{ borderTopColor: "var(--club)" }} data-testid="pot-club">
              <div className="eyebrow" style={{ color: "var(--club-dark)" }}>Wine Club credit</div>
              <div className="mt-3 font-serif text-4xl tabular-nums tracking-tight lg:text-5xl" data-testid="club-balance">{wallet ? money(wallet.club_balance_pence) : "—"}</div>
              <p className="mt-3 text-xs leading-relaxed text-neutral-600">Your regular case, built by your Wine Club Direct Debit. Spent on each quarterly case first — anything left is still yours to spend.</p>
              <Link to="/club" className="mt-3 inline-block text-xs underline underline-offset-4" data-testid="pot-club-link">Wine Club plan →</Link>
            </div>
          </div>
          <div className="mt-4 text-sm text-neutral-600">Together: <span className="font-serif text-lg tabular-nums text-[var(--ink)]" data-testid="total-balance">{wallet ? money(wallet.total_pence) : "—"}</span></div>
          {config?.gocardless_mock && (
            <p className="mt-6 border border-dashed border-border p-3 text-xs text-neutral-500" data-testid="sandbox-notice">Sandbox mode: GoCardless is simulated. Use the “Simulate” controls to confirm or fail payments as the bank would.</p>
          )}
          {config && !config.gocardless_mock && config.gocardless_environment === "live" && (
            <p className="mt-6 border border-[var(--wine)] bg-[var(--wine)]/5 p-3 text-xs text-[var(--wine)]" data-testid="live-notice">
              LIVE Direct Debit: mandates and collections here are real GoCardless payments from your bank account.
              {!config.gocardless_webhook_configured && " Webhook not yet configured — credits will not post until it is."}
            </p>
          )}
        </div>
        <div className="lg:col-span-7">
          <MandateCard />
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mt-14">
        <TabsList className="h-auto w-full justify-start gap-6 overflow-x-auto rounded-none border-b border-border bg-transparent p-0">
          {CREDIT_TABS.map(([v, label]) => (
            <TabsTrigger key={v} value={v} className="rounded-none border-b-2 border-transparent px-0 pb-3 text-sm data-[state=active]:border-[var(--ink)] data-[state=active]:bg-transparent data-[state=active]:shadow-none" data-testid={`cellar-tab-${v}`}>{label}</TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="topup" className="mt-10 grid gap-12 lg:grid-cols-2">
          {config && <TopUpPanel mandateActive={mandateActive} loaded={!loading} cardEnabled={config.card_payments} stripeTest={config.stripe_mode === "test"} />}
          <PaymentsList mock={!!config?.gocardless_mock} />
        </TabsContent>
        <TabsContent value="auto" className="mt-10"><AutoTopUpPanel wallet={wallet} disabled={!mandateActive} /></TabsContent>
        <TabsContent value="plan" className="mt-10 space-y-12"><PlanSummary /><SubscriptionPanel disabled={loading || !mandateActive} mock={!!config?.gocardless_mock} /></TabsContent>
        <TabsContent value="ledger" className="mt-10"><LedgerTable balancePence={wallet?.balance_pence} /></TabsContent>
        <TabsContent value="gifts" className="mt-10"><GiftPanel balance={wallet?.balance_pence ?? 0} loaded={!!wallet} /></TabsContent>
        <TabsContent value="refer" className="mt-10"><ReferPanel /></TabsContent>
        <TabsContent value="password" className="mt-10"><ChangePasswordPanel /></TabsContent>
      </Tabs>
    </section>
  );
}
